/* eslint-disable react/jsx-props-no-spreading */
import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import isSuperAdmin from 'utils/isSuperAdmin';
import { getBeneficiaryFDPercent } from 'utils/filters';

import Page from 'modules/_shared/components/Pages/Page';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';
import { setModal } from 'modules/_shared/redux/modalActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import CustomDataTable from 'modules/_shared/components/Tables/CustomDataTable';
import OperationMenuDots from 'modules/_shared/components/MenuDots/OperationMenuDots';

import UploadBeneficiary from 'modules/beneficiaries/modals/UploadBeneficiary';
import DownloadBeneficiary from 'modules/beneficiaries/modals/DownloadBeneficiary';
import BeneficiaryListMetrics, {
  SummaryRow,
} from 'modules/beneficiaries/components/BeneficiaryListMetrics';

import useBeneficiaryList from './hooks/useBeneficiaryList';

const BeneficiaryList: FC = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { planId } = useParams();

  const user = useSelector((state: State) => state.user);
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const { data, actions, isLoading } = useBeneficiaryList();

  const {
    rows,
    summaryRow,
    percentVestedUnits,
    tableData,
    rawData,
    currentTableColumns,
    currentMenuOptions,
    pendingInvitations,
    isAdmin,
    isReadOnly,
  } = data;

  const {
    getPlanName,
    getUnits,
    getToVest,
    getMonthsToVest,
    getStrikePrice,
    getCertificateData,
    getStatus,
    getStatusName,
    getPageDescription,
    handleDeleteBeneficiaries,
  } = actions;

  return (
    <Page id="beneficiary-list-page">
      <PageListHeader
        title={actualSociety?.name}
        subTitle={t('Beneficiaries')}
        subTitleSuffix={planId ? getPlanName(planId) : ''}
        description={getPageDescription()}
        actions={[
          isSuperAdmin(user) && data.currentPlan && (
            <button
              type="button"
              className="btn btn-danger "
              onClick={handleDeleteBeneficiaries}
            >
              <span>{t('DeleteBeneficiaries')}</span>
            </button>
          ),
          isAdmin && (
            <button
              type="button"
              className="btn btn-white btn-outline-light"
              onClick={() => dispatch(setModal(<UploadBeneficiary />))}
              disabled={isReadOnly}
            >
              <em className="icon ni ni-upload-cloud" />
              <span>{t('UploadListing')}</span>
            </button>
          ),
          isAdmin && (
            <button
              type="button"
              className="btn btn-white btn-outline-light"
              onClick={() =>
                dispatch(
                  setModal(
                    <DownloadBeneficiary
                      actualSociety={actualSociety}
                      plans={data.plans}
                      tableData={rawData}
                      getStatus={getStatus}
                      getStatusName={getStatusName}
                      getUnits={getUnits}
                      getBeneficiaryFDPercent={getBeneficiaryFDPercent}
                      getIncentive={actions.getIncentive}
                      getPlanName={getPlanName}
                      getToVest={getToVest}
                      getMonthsToVest={getMonthsToVest}
                      getStrikePrice={getStrikePrice}
                      getCertificateData={getCertificateData}
                      getConsolidatedPercent={actions.getConsolidatedPercent}
                      getConsolidationDate={actions.getConsolidationDate}
                    />
                  )
                )
              }
            >
              <em className="icon ni ni-download-cloud" />
              <span>{t('DownloadListing')}</span>
            </button>
          ),
          isAdmin && (
            <OperationMenuDots
              menuOptions={currentMenuOptions}
              // @ts-ignore
              handleModal={(Modal) =>
                dispatch(
                  setModal(
                    <Modal user={user} societyId={actualSociety['_id']} />
                  )
                )
              }
              disabled={isReadOnly}
            />
          ),
        ].filter(Boolean)}
      />

      {tableData.length > 0 && tableData.length === rows.length && (
        <>
          <BeneficiaryListMetrics
            isAdmin={isAdmin}
            summaryRow={summaryRow as unknown as SummaryRow}
            pendingInvitations={pendingInvitations}
            percentVestedUnits={percentVestedUnits}
          />

          <CustomDataTable
            data={tableData}
            columns={currentTableColumns}
            searchBy={[t('Beneficiary'), t('Email'), t('CIF')]}
            searchByPlaceholder={[t('name'), t('email'), t('CIF')].join(', ')}
            pagination
            className="nk-tb-list"
            // expandableRows
            actions
            showDense
            fileName={`${t('BeneficiariesList')} ${actualSociety?.name}`}
            toggleNationality={undefined}
            onFilterChange={undefined}
          />
        </>
      )}

      {isLoading && <CustomLoading />}
    </Page>
  );
};

export default BeneficiaryList;
